import { HelpButton } from "@/components/help-button";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { DialogHeader, DialogFooter } from "@/components/ui/dialog";
import {
  aOrAn,
  formatNumber,
  plural,
  shorten,
  showTimeDiffPartial,
} from "@/lib/utils";
import { DSFarmParams, FarmState } from "@myth-finance/dualstake-farm-sdk";
import {
  DSContractState,
  DSContractListing,
} from "@myth-finance/dualstake-ts-sdk";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { cx } from "class-variance-authority";
import {
  ArrowRightIcon,
  CalculatorIcon,
  ChevronDown,
  ChevronRight,
  CircleDollarSign,
  ClockArrowUp,
  Hammer,
  Plus,
  Sprout,
  UserRound,
  Users,
} from "lucide-react";
import { ChangeEventHandler, useEffect, useMemo, useState } from "react";
import { ApplicationLink, BlockLink, Link } from "@/components/links";
import {
  getFarmCreationCostAndDuration,
  makeCreateFarmTransactions,
  makeExtendAmountTransactions,
  makeExtendDurationTransactions,
} from "@/lib/farm-client";
import { ConnectButton } from "@/components/connect-button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ErrorComponent } from "@/components/error-component";
import {
  TransactionDialog,
  transactionInner,
} from "@/components/transaction/transaction-dialog";
import { useWallet } from "@txnlab/use-wallet";
import { z } from "zod";
import {
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import { twMerge } from "tailwind-merge";
import { dsFarmAppId } from "@/config";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "@/components/ui/collapsible";
import { getHighlightClasses } from "@/lib/get-tokens-highlights";
import { useAlgoAcctInfo, useBalance } from "@/hooks/use-algo-account";
import {
  clearAPRCache,
  FullAprBreakdown,
  useAprBreakdown,
} from "@/hooks/use-apr-breakdown";
import { Loading } from "@/components/loading";
import { clearFarmsAndAPRCache } from "@/hooks/use-farms-and-aprs";
import { DSFarmStateExtended } from "@/lib/types";
import { useBlockTime } from "@/hooks/use-block-time";

export function FarmCard({
  className,
  farm: farmState,
  cState,
  listing,
  aprBreakdown,
}: {
  className?: string;
  farm?: DSFarmStateExtended;
  cState: DSContractState;
  listing: DSContractListing;
  aprBreakdown: FullAprBreakdown;
}) {
  if (!cState || !listing) return null;
  const [open, setOpen] = useState(false);
  const textColors = getHighlightClasses(listing.asaId, "text");
  return (
    <Card
      className={cx("w-[350px] max-w-[100%] relative self-stretch", className)}
    >
      <CardHeader className="opacity-0 animate-fade-in-2">
        <CardTitle className="flex items-center gap-1">
          <Sprout className="relative -top-[2px] inline" />{" "}
          {farmState ? (
            <>
              <span>{listing.asaUnitName} Farm </span>{" "}
              <span className={textColors}>Active</span>
            </>
          ) : (
            `Set up ${listing.asaUnitName} farm`
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col items-stretch text-foreground opacity-0 animate-fade-in-2 gap-1">
        {farmState ? (
          <>
            <FarmDisplayInner
              listing={listing}
              farmState={farmState}
              aprBreakdown={aprBreakdown}
            />
          </>
        ) : (
          <>
            <div>
              {listing.asaUnitName} bulls can donate {listing.asaUnitName} to
              create a farm for the {listing.lstName} token.
            </div>
          </>
        )}
      </CardContent>
      <Sprout
        size={192}
        strokeWidth="1.25px"
        className="absolute pointer-events-none z-0 top-[50%] left-[50%] -translate-x-[50%] -translate-y-[50%] opacity-5"
      />
      <CardFooter className="flex justify-between items-center gap-2 relative z-1 opacity-0 animate-fade-in-2">
        <FarmHelpIcon listing={listing} />
        <FarmDialog
          open={open}
          setOpen={setOpen}
          cState={cState}
          listing={listing}
          farmState={farmState}
          aprBreakdown={aprBreakdown}
        >
          <Button variant="outline" onClick={() => setOpen(true)}>
            {farmState ? "View Details" : "Create Farm"}
          </Button>
        </FarmDialog>
      </CardFooter>
    </Card>
  );
}

const FarmHelpIcon = ({
  listing,
  className,
}: {
  className?: string;
  listing: DSContractListing;
}) => {
  const textColors = getHighlightClasses(listing.asaId, "text");
  return (
    <HelpButton triggerClassName={className} title="What are farms?">
      <div>
        dualSTAKE farms pay out an extra{" "}
        <span className={textColors}>{listing.asaUnitName}</span> bonus for each
        block produced by the{" "}
        <span className={textColors}>{listing.lstName}</span> node.
      </div>
      <div>
        This extra <span className={textColors}>{listing.asaUnitName}</span> is
        added proportionately to the value of each circulating{" "}
        <span className={textColors}>{listing.lstName}</span>, resulting in a
        higher APR for <span className={textColors}>{listing.lstName}</span>{" "}
        holders.
      </div>
      <div>
        <span className="text-muted-foreground">
          dualSTAKE farms are permissionless. Anyone can create or extend a farm
          at any time.
        </span>
      </div>
      <div className="text-muted-foreground">
        Read more about farming{" "}
        <Link to="https://docs.myth.finance/dualSTAKE/farming">here</Link>.
      </div>
    </HelpButton>
  );
};

function FarmDisplayInner({
  long,
  farmState,
  listing,
  aprBreakdown,
}: {
  long?: true;
  farmState: DSFarmStateExtended;
  listing: DSContractListing;
  aprBreakdown: FullAprBreakdown;
}) {
  const { remainingDurationSec } = farmState ?? {};
  const textColors = getHighlightClasses(listing.asaId, "text");
  const { isPending, data: lastBlockPaidTime } = useBlockTime(
    long ? farmState?.lastBlockPaid ?? 0n : 0n
  );
  return (
    <>
      <div className="flex justify-between">
        <div className="transition-all text-muted-foreground">Farm APR</div>
        {formatNumber(aprBreakdown?.farmAprBps, 2)} %
      </div>

      <div className="flex justify-between">
        <div className="transition-all text-muted-foreground">Block Bonus</div>
        <div className="text-right">
          {formatNumber(
            farmState?.amountPerBlock!,
            listing.asaDecimals,
            listing.asaDecimals
          )}{" "}
          {listing.asaUnitName}
        </div>
      </div>

      <div className="flex justify-between">
        <div className="transition-all text-muted-foreground">
          Remaining Duration
        </div>
        <div>
          {Number(farmState?.remainingDurationBlocks)}{" "}
          {long ? <>Proposed</> : null}{" "}
          {plural("Block", farmState?.remainingDurationBlocks)}
        </div>
      </div>

      <div className="flex justify-between items-center">
        <div className="transition-all text-muted-foreground">
          Estimated Expiry
        </div>
        <div className="flex items-center gap-3 text-right">
          <HelpButton title="Farm Expiry Estimate">
            <div>
              The farm pays out upon each{" "}
              <span className={textColors}>{listing.lstName}</span> block
              proposal.
            </div>
            <div>
              It will last for the next{" "}
              <span className={textColors}>
                {Number(farmState?.remainingDurationBlocks)}{" "}
                {plural("block", farmState?.remainingDurationBlocks)}
              </span>{" "}
              produced by {listing.lstName}.
            </div>
            <div>
              Based on the average round time of the network and{" "}
              {listing.lstName}
              's percentage of stake, the best estimate for expiry is{" "}
              <span className={textColors}>
                {showTimeDiffPartial(Number(1000n * remainingDurationSec), 2)}
              </span>
              .
            </div>
            <div>
              This is{" "}
              <span className={textColors}>provided only as an indication</span>
              , as randomness in block proposals is built in to Algorand
              consensus mechanism.
            </div>
            <div className="text-muted-foreground">
              You can find more information on block production variance
              in&nbsp;
              <Link to="https://valar-staking.medium.com/where-is-my-block-70113da4d817">
                this blog post
              </Link>{" "}
              from the great folks at Valar Staking.
            </div>
          </HelpButton>
          {!remainingDurationSec
            ? null
            : showTimeDiffPartial(
                Number(1000n * remainingDurationSec),
                long ? 2 : 1
              )}
        </div>
      </div>

      {!long ? null : (
        <>
          <div className="flex justify-between">
            <div className="transition-all text-muted-foreground">
              Last block
            </div>
            <div className="flex gap-3 items-center">
              <HelpButton title="Farm Last Block">
                <div>
                  In most cases this will show the{" "}
                  <span className={textColors}>
                    last block that was paid out
                  </span>{" "}
                  by the farm.
                </div>
                <div>
                  The exception is when a farm is first created, in which case
                  it will display the{" "}
                  <span className={textColors}>farm creation block</span> until
                  the first farm block is proposed.
                </div>
              </HelpButton>{" "}
              <BlockLink id={farmState?.lastBlockPaid} />
            </div>
          </div>

          <div className="flex justify-between">
            <div className="transition-all text-muted-foreground"></div>
            <div>
              {isPending || !lastBlockPaidTime ? (
                <>&nbsp;</>
              ) : (
                <>
                  {showTimeDiffPartial(
                    +new Date() - lastBlockPaidTime * 1000,
                    2
                  )}{" "}
                  ago
                </>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}

type CreateDialogMode = "create" | "extenda" | "extendd";
type FarmDialogMode = "index" | CreateDialogMode;

export function FarmDialog({
  open,
  setOpen,
  listing,
  cState,
  farmState,
  aprBreakdown,
  children,
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  cState: DSContractState;
  listing: DSContractListing;
  farmState?: DSFarmStateExtended;
  aprBreakdown: FullAprBreakdown;
  children: React.ReactNode;
}) {
  const [mode, setMode] = useState<FarmDialogMode>("index");

  useEffect(() => {
    if (open) setMode("index");
  }, [open]);

  const childProps = { listing, cState, setMode };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="gap-2 flex flex-col max-h-[100svh] overflow-y-auto">
        {mode === "index" ? (
          !farmState ? (
            <FarmEmptyIndex
              listing={listing}
              setMode={setMode}
              setOpen={setOpen}
            />
          ) : (
            <FarmIndex
              farmState={farmState}
              listing={listing}
              setMode={setMode}
              setOpen={setOpen}
              aprBreakdown={aprBreakdown}
            />
          )
        ) : mode === "create" || mode === "extendd" || mode === "extenda" ? (
          <FarmCreate
            {...childProps}
            mode={mode}
            setOpen={setOpen}
            farmState={farmState}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function FarmTitle({ children }: { children: React.ReactNode }) {
  return (
    <DialogHeader>
      <DialogTitle className="mb-2 text-center">{children}</DialogTitle>
    </DialogHeader>
  );
}

function FarmIndex({
  listing,
  farmState,
  setMode,
  setOpen,
  aprBreakdown,
}: {
  listing: DSContractListing;
  farmState: DSFarmStateExtended;
  setMode: React.Dispatch<React.SetStateAction<FarmDialogMode>>;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  aprBreakdown: FullAprBreakdown;
}) {
  const [showExtend, setShowExtend] = useState(false);
  const { activeAddress } = useWallet();
  useEffect(() => setShowExtend(false), []);

  return (
    <>
      <FarmTitle>
        {showExtend ? "Extend " : ""}
        {listing.asaUnitName} farm
      </FarmTitle>

      <div className="mt-2 bg-card border p-4 self-center rounded-lg flex flex-col gap-2 w-full lg:w-96">
        <FarmDisplayInner
          long={true}
          listing={listing}
          farmState={farmState}
          aprBreakdown={aprBreakdown}
        />
      </div>

      {showExtend ? (
        <div className="flex flex-col w-full mt-3 gap-2">
          <div>
            You can extend the {listing.asaUnitName} farm by amount or duration.
          </div>
          <div className="text-muted-foreground">
            If you extend by amount, you will increase the {listing.asaUnitName}{" "}
            amount per block paid into {listing.lstName}, as well as the APR.
          </div>
          <div className="text-muted-foreground">
            If you extend by duration, you will increase the longevity of the
            farm.
          </div>
        </div>
      ) : null}

      <DialogFooter className="mt-4 gap-2 items-center">
        {showExtend ? (
          <div className="flex items-center justify-between w-full">
            <FarmHelpIcon className="flex" listing={listing} />

            <div className="gap-2 flex items-center">
              {activeAddress ? (
                <>
                  <Button onClick={() => setMode("extenda")}>
                    <CircleDollarSign />
                    Extend Amount
                  </Button>
                  <Button onClick={() => setMode("extendd")}>
                    <ClockArrowUp /> Extend Duration
                  </Button>{" "}
                </>
              ) : (
                <ConnectButton
                  onDisconnectedClick={() => {
                    setOpen(false);
                  }}
                />
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between w-full">
            <FarmHelpIcon className="flex" listing={listing} />

            <div className="gap-2 flex">
              <Button onClick={() => alert("Coming soon")} variant="secondary">
                <Users /> View Donors
              </Button>

              <Button onClick={() => setShowExtend(true)}>
                <Plus /> Extend farm
              </Button>
            </div>
          </div>
        )}
      </DialogFooter>
    </>
  );
}

function FarmEmptyIndex({
  listing,
  setMode,
  setOpen,
}: {
  listing: DSContractListing;
  setMode: React.Dispatch<React.SetStateAction<FarmDialogMode>>;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  return (
    <>
      <FarmTitle>{listing.asaUnitName} farm</FarmTitle>
      <div>{listing.asaUnitName} farm is not set up.</div>
      <div className="text-muted-foreground text-sm">
        Anyone can donate to set up or extend a farm. Farms pay{" "}
        {listing.asaUnitName} into {listing.lstName} upon block production,
        increasing the {listing.lstName} holders' APR.
      </div>
      <DialogFooter className="sm:justify-end mt-4 gap-2">
        <DialogClose asChild>
          <Button variant="outline">Close</Button>
        </DialogClose>
        <ConnectButton
          onDisconnectedClick={() => {
            setOpen(false);
          }}
          onClick={() => setMode("create")}
        >
          Create farm
        </ConnectButton>
      </DialogFooter>
    </>
  );
}

function makeAmountSchema(
  farmState: FarmState | undefined,
  mode: CreateDialogMode,
  listing: DSContractListing,
  userAsaBalance: number
) {
  const { asaDecimals, asaUnitName } = listing;
  return z
    .string()
    .refine(
      (val) => {
        if (!val) return true;
        const num = Number(val);
        return !isNaN(num) && num >= 0;
      },
      { message: "Please enter a valid positive amount" }
    )
    .refine(
      (val) => {
        if (!val) return true;
        if (mode !== "extenda") return true;
        const num = Number(val);
        const baseNum = num * 10 ** asaDecimals;
        const existingAmount = farmState?.amountPerBlock;
        return !isNaN(num) && baseNum > Number(existingAmount);
      },
      {
        message: `Amount must be higher than existing ${asaUnitName} per block`,
      }
    )
    .refine(
      (val) => {
        if (!val) return true;
        if (mode !== "extenda") return true;
        const duration = Number(farmState?.remainingDurationBlocks);
        const num = Number(val);
        const baseNum = num * 10 ** asaDecimals;
        const existingAmount = Number(farmState?.amountPerBlock);
        const diff = (baseNum - existingAmount) * duration;
        const balance = userAsaBalance;
        return !isNaN(num) && balance >= diff;
      },
      { message: `Not enough ${asaUnitName} in your wallet` }
    );
}

function makeDurationSchema(
  farmState: FarmState | undefined,
  farmCost: DSFarmParams | undefined,
  mode: CreateDialogMode
) {
  return z
    .string()
    .refine(
      (val) => {
        if (val === "") return true;
        const num = Number(val);
        return !isNaN(num) && num >= 0;
      },
      { message: "Please enter a valid positive amount" }
    )
    .refine(
      (val) => {
        if (val === "") return true;
        const num = Number(val);
        return num === parseInt(val, 10);
      },
      { message: "Please enter an integer" }
    )
    .refine(
      (val) => {
        if (val === "") return true;
        if (typeof farmCost === "string" || !farmCost?.totalCost) return true;
        const num = BigInt(val);
        const { maxDuration } = farmCost;
        return num <= maxDuration;
      },
      {
        message:
          typeof farmCost === "object"
            ? `Max allowed duration: ${Number(
                (farmCost as DSFarmParams).maxDuration
              )}`
            : "ERR NEVER",
      }
    )
    .refine(
      (val) => {
        if (!val) return true;
        if (mode !== "extendd") return true;
        const num = Number(val);
        const existingDuration = Number(farmState?.remainingDurationBlocks);
        return !isNaN(num) && num > existingDuration;
      },
      {
        message: `New Duration must be minimum ${
          1 + Number(farmState?.remainingDurationBlocks)
        } blocks`,
      }
    );
}

function FarmCreate({
  listing,
  cState,
  setMode,
  mode,
  farmState,
}: {
  listing: DSContractListing;
  cState: DSContractState;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setMode: React.Dispatch<React.SetStateAction<FarmDialogMode>>;
  farmState: FarmState | undefined;
  mode: CreateDialogMode;
}) {
  const { activeAddress } = useWallet();
  const { asaId, asaDecimals, asaUnitName, lstName, appId } = listing ?? {};

  const [durationBlocks, setDurationBlocks] = useState<string>("");
  const [amountPerBlock, setAmountPerBlock] = useState<string>(
    mode === "extendd"
      ? String(
          Math.round(Number(farmState!.amountPerBlock) / 10 ** asaDecimals)
        )
      : ""
  );
  const [amountError, setAmountError] = useState<string>();
  const [durationError, setDurationError] = useState<string>();
  const [costError, setCostError] = useState<string>("");

  const [farmCost, setFarmCost] = useState<DSFarmParams | undefined>();
  const [aprBreakdownReq, setAprBreakdownReq] = useState<bigint>(0n);
  const [apiError, setApiError] = useState<string>("");
  const [apiLoading, setApiLoading] = useState(false);

  const { data: accountInfo } = useAlgoAcctInfo(activeAddress);
  const userAlgoBalance = useBalance(1, accountInfo);
  const userAsaBalance = useBalance(asaId, accountInfo);
  const [maxDuration, setMaxDuration] = useState(0n);

  useEffect(() => {
    (async () => {
      const farmCost = await getFarmCreationCostAndDuration({
        dsAppId: appId,
        farmAssetId: asaId,
        durationBlocks: 1n,
      });
      if (typeof farmCost !== "string") {
        setMaxDuration(farmCost.maxDuration);
      }
    })();
  }, [farmState?.appId, mode]);

  const durationForCost = useMemo(() => {
    // wrapping because for inputs like 3, the validation runs after this, which errors on BigInt()
    try {
      return durationError
        ? 1n
        : mode === "extendd"
        ? BigInt(durationBlocks) - farmState!.remainingDurationBlocks
        : mode === "create"
        ? BigInt(durationBlocks)
        : 1n;
    } catch (e) {}
  }, [mode, farmState?.remainingDurationBlocks, durationError, durationBlocks]);

  const durationSchema = useMemo(
    () => makeDurationSchema(farmState, farmCost, mode),
    [farmState, farmCost, mode]
  );

  const amountSchema = useMemo(
    () => makeAmountSchema(farmState, mode, listing, userAsaBalance),
    [farmState, mode, listing, userAsaBalance]
  );

  useEffect(() => {
    const durationValidation = durationSchema.safeParse(durationBlocks);
    if (!durationValidation.success) {
      setDurationError(durationValidation.error.errors[0].message);
    } else {
      setDurationError(undefined);
    }
    const amountValidation = amountSchema.safeParse(amountPerBlock);
    if (!amountValidation.success) {
      setAmountError(amountValidation.error.errors[0].message);
    } else {
      setAmountError(undefined);
    }
  }, [amountPerBlock, durationBlocks, farmCost]);

  const changeAmountPerBlock: ChangeEventHandler<HTMLInputElement> = (amt) => {
    setAprBreakdownReq(0n);
    setFarmCost(undefined);
    setAmountPerBlock(amt.currentTarget.value);
    setApiError("");
  };

  const changeDurationBlocks: ChangeEventHandler<HTMLInputElement> = (amt) => {
    setAprBreakdownReq(0n);
    setFarmCost(undefined);
    setDurationBlocks(amt.currentTarget.value);
    setApiError("");
  };

  const fapb =
    mode === "extendd"
      ? farmState!.amountPerBlock
      : amountPerBlock
      ? BigInt(Math.round(Number(amountPerBlock) * 10 ** asaDecimals))
      : undefined;

  const { error: aprError, data: aprBreakdown } = useAprBreakdown(
    { ...cState, appId: aprBreakdownReq },
    fapb
  );

  const calculateApr = () => {
    setAprBreakdownReq(cState.appId);
  };

  const calculateCost = () => {
    setApiLoading(true);
    if (mode !== "extendd") calculateApr();
    (async () => {
      setCostError(``);
      const farmDuration =
        mode === "extendd"
          ? BigInt(durationBlocks!) - farmState!.remainingDurationBlocks
          : BigInt(durationBlocks!);
      const cost =
        mode === "extenda"
          ? undefined
          : await getFarmCreationCostAndDuration({
              dsAppId: appId,
              farmAssetId: asaId,
              durationBlocks: farmDuration,
            });
      setApiLoading(false);
      if (typeof cost !== "string") {
        const amtPerBlock =
          mode === "extendd"
            ? farmState!.amountPerBlock
            : BigInt(
                Math.round(Number(amountPerBlock) * 10 ** asaDecimals)
              );
        if (BigInt(userAsaBalance) < (durationForCost ?? 0n) * amtPerBlock) {
          setCostError(`Not enough ${asaUnitName}`);
        }
        if (!cost) {
          return;
        }
        setFarmCost(cost);
        setMaxDuration(cost.maxDuration);
        const algoBalance = userAlgoBalance;

        if (algoBalance < cost.totalCost) {
          setCostError(`Not enough ALGO`);
        }
      } else {
        setFarmCost(undefined);
        setApiError(cost);
      }
    })();
  };
  const onSuccess = () => {
    clearFarmsAndAPRCache();
    clearAPRCache(cState.appId);
    setMode("index");
  };
  const formattedAmount =
    typeof farmCost === "object"
      ? formatNumber(farmCost.totalCost, 6, 6)
      : undefined;
  const formattedAsaAmount =
    typeof farmCost === "object" && mode !== "extendd"
      ? formatNumber(
          parseInt(durationBlocks!, 10) * Number(amountPerBlock!),
          0,
          asaDecimals
        )
      : typeof farmCost === "object" && mode === "extendd"
      ? formatNumber(
          (parseInt(durationBlocks!, 10) -
            Number(farmState!.remainingDurationBlocks)) *
            Number(farmState!.amountPerBlock),
          asaDecimals,
          asaDecimals
        )
      : undefined;

  const extendLabel = mode === "extenda" ? "amount" : "duration";

  const extendAmountCost =
    mode === "extenda"
      ? (Number(amountPerBlock) -
          Number(farmState?.amountPerBlock) / 10 ** asaDecimals) *
        Number(farmState?.remainingDurationBlocks) *
        10 ** asaDecimals
      : 0;

  return (
    <>
      {mode === "create" ? (
        <>
          <FarmTitle>Create {asaUnitName} farm</FarmTitle>
          <div className="mb-1">
            Donate {asaUnitName} to start {aOrAn(lstName)} {lstName} farm.
            Experiment with different values to see the resulting APR.
          </div>
        </>
      ) : (
        <>
          <FarmTitle>
            Extend {asaUnitName} farm {extendLabel}
          </FarmTitle>
          <div className="mb-2 text-left">
            {extendLabel === "duration" ? (
              <>
                Increase the {extendLabel} of the {lstName} farm.{" "}
                <span className="text-muted-foreground">
                  Farm duration is measured in blocks produced by the {lstName}{" "}
                  node.
                </span>
              </>
            ) : (
              <>
                Increase the {asaUnitName} amount paid by the {lstName} farm for
                evey produced block.
              </>
            )}
          </div>
        </>
      )}

      <div className="self-center flex flex-col gap-2 items-stretch w-80">
        {mode === "create" ? null : (
          <div
            className={cx(
              "flex gap-1 mb-2",
              mode === "extenda" ? "flex-col" : "flex-col-reverse"
            )}
          >
            <div className="text-sm flex justify-between">
              <div className="text-muted-foreground">Duration</div>
              <div>
                {Number(farmState?.remainingDurationBlocks)} proposed blocks
              </div>
            </div>
            <div className="text-sm flex justify-between">
              <div className="text-muted-foreground">
                {asaUnitName} per block
              </div>
              <div>{Number(farmState?.amountPerBlock) / 10 ** asaDecimals}</div>
            </div>
          </div>
        )}
        {mode !== "extenda" ? (
          <div className="">
            <Label className="inline-flex w-full justify-between items-center">
              <span>
                {mode === "extendd" ? "New" : undefined} Duration{" "}
                <span className="text-muted-foreground">
                  in proposed blocks
                </span>
              </span>
              {!maxDuration ? null : (
                <span>Max: {Number(farmCost?.maxDuration ?? maxDuration)}</span>
              )}
            </Label>
            <TooltipProvider>
              <Tooltip open={!!durationError}>
                <TooltipTrigger asChild>
                  <Input
                    autoFocus
                    disabled={apiLoading}
                    value={durationBlocks ?? ""}
                    onChange={changeDurationBlocks}
                    className={durationError ? "border-destructive" : ""}
                  />
                </TooltipTrigger>
                <TooltipContent side="bottom" className={`bg-destructive`}>
                  {durationError}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        ) : null}
        {mode !== "extendd" ? (
          <div>
            <Label>
              {mode === "extenda" ? "New" : "Farm"} Amount{" "}
              <span className="text-muted-foreground">
                {asaUnitName} per block
              </span>
            </Label>
            <TooltipProvider>
              <Tooltip open={!!amountError}>
                <TooltipTrigger asChild>
                  <Input
                    autoFocus={mode === "extenda"}
                    disabled={apiLoading}
                    value={amountPerBlock ?? ""}
                    onChange={changeAmountPerBlock}
                    className={amountError ? "border-destructive" : ""}
                  />
                </TooltipTrigger>
                <TooltipContent side="bottom" className={`bg-destructive`}>
                  {amountError}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        ) : null}
      </div>

      {!(apiError || aprError) ? null : (
        <div className="mt-4 bg-card border p-4 pt-2 pb-5 rounded-lg flex flex-col gap-2">
          <ErrorComponent error={apiError ?? aprError?.message} />
        </div>
      )}

      {farmCost || aprBreakdown ? (
        <div
          className={twMerge(
            "mt-4 bg-card border p-4 py-2 rounded-lg flex flex-col gap-2",
            costError ? "border-destructive" : ""
          )}
        >
          {!costError ? null : (
            /* Cost error */ <div className="text-destructive self-center font-bold">
              Error: {costError}
            </div>
          )}

          {mode !== "extenda" ? null : (
            /* Extend amount */ <>
              <div className="text-muted-foreground -mb-1">
                Farm amount per block
              </div>
              <div className="self-center text-muted-foreground flex items-center gap-2">
                {extendAmountCost && !amountError ? (
                  <>
                    {formatNumber(
                      farmState?.amountPerBlock ?? 0,
                      asaDecimals,
                      asaDecimals
                    )}{" "}
                    {asaUnitName} <ArrowRightIcon size={16} />{" "}
                    <span className="text-foreground">
                      {amountPerBlock} {asaUnitName}
                    </span>
                  </>
                ) : (
                  "-"
                )}
              </div>
              <div className="text-muted-foreground -mb-1">Total cost</div>
              <div className="self-center flex flex-col items-center">
                <div className="text-muted-foreground text-sm">
                  {formatNumber(
                    Number(
                      extendAmountCost /
                        Number(farmState?.remainingDurationBlocks || 1)
                    ),
                    asaDecimals,
                    asaDecimals
                  )}{" "}
                  {asaUnitName} x {Number(farmState?.remainingDurationBlocks)}{" "}
                  blocks
                </div>
                <div className="">
                  {amountError || !extendAmountCost
                    ? "-"
                    : `${formatNumber(
                        extendAmountCost,
                        asaDecimals,
                        asaDecimals
                      )} ${asaUnitName}`}
                </div>
              </div>
            </>
          )}

          {mode !== "extendd" ? null : (
            /* Extend amount */ <>
              <div className="text-muted-foreground -mb-1">
                Extending Duration
              </div>
              <div className="self-center text-muted-foreground flex items-center gap-2">
                <>
                  {Number(farmState!.remainingDurationBlocks)} blocks{" "}
                  <ArrowRightIcon size={16} />{" "}
                  <span className="text-foreground">
                    {durationBlocks} blocks
                  </span>
                </>
              </div>
            </>
          )}

          {farmCost ? (
            <>
              <div className="text-muted-foreground">
                Total cost to {mode === "create" ? "create farm" : "extend"}
              </div>
              <div className="self-center">
                {formattedAsaAmount} {asaUnitName} + {formattedAmount} ALGO
              </div>
              <Collapsible className="group self-end data-[state=open]:self-start data-[state=open]:border data-[state=open]:p-2 data-[state=open]:pl-4 data-[state=open]:rounded-lg">
                <CollapsibleTrigger className="flex items-center text-muted-foreground -ml-3 text-sm">
                  <Button variant="ghost" size="sm">
                    <ChevronDown className="h-4 w-4 group-data-[state=closed]:hidden" />
                    <ChevronRight className="h-4 w-4 group-data-[state=open]:hidden" />
                    <span className="sr-only">Toggle</span>
                  </Button>
                  <h4>Cost breakdown</h4>
                </CollapsibleTrigger>
                <CollapsibleContent className="text-sm flex flex-col gap-1">
                  <div className="mt-1 text-muted-foreground">
                    Dynamic costs:
                  </div>
                  <div className="ml-2">
                    - Farm rewards:{" "}
                    {Number(durationForCost) * Number(amountPerBlock)}{" "}
                    {asaUnitName}{" "}
                    <span className="text-muted-foreground">
                      ({amountPerBlock} {asaUnitName} / block)
                    </span>
                  </div>
                  <div className="ml-2">
                    - Execution incentives:{" "}
                    {formatNumber(farmCost.details.ixCost, 6, 6)} ALGO{" "}
                    <span className="text-muted-foreground">
                      (
                      {formatNumber(
                        farmCost.details.ixCost / durationForCost!,
                        6,
                        6
                      )}{" "}
                      ALGO / block)
                    </span>
                  </div>
                  <div className="ml-2">
                    - Platform fees:{" "}
                    {formatNumber(farmCost.details.platformCost, 6, 6)} ALGO{" "}
                    <span className="text-muted-foreground">
                      (
                      {formatNumber(
                        farmCost.details.platformCost / durationForCost!,
                        6,
                        6
                      )}{" "}
                      ALGO / block)
                    </span>
                  </div>
                  <div className="ml-2">
                    - Transaction costs:{" "}
                    {formatNumber(farmCost.details.txnFeeCost, 6, 6)} ALGO{" "}
                    <span className="text-muted-foreground">
                      (
                      {formatNumber(
                        farmCost.details.txnFeeCost / durationForCost!,
                        6,
                        6
                      )}{" "}
                      ALGO / block)
                    </span>
                  </div>
                  {!!farmCost.details.optinCost ||
                  !!farmCost.details.boxCost ? (
                    <>
                      <div className="mt-2 text-muted-foreground">
                        Fixed costs:
                      </div>
                      {!farmCost.details.optinCost ? null : (
                        <div className="ml-2">
                          - ASA opt-in cost:{" "}
                          {formatNumber(farmCost.details.optinCost, 6, 6)} ALGO
                        </div>
                      )}
                      {!farmCost.details.boxCost ? null : (
                        <div className="ml-2">
                          - Box Storage:{" "}
                          {formatNumber(farmCost.details.boxCost, 6, 6)} ALGO
                        </div>
                      )}
                    </>
                  ) : null}
                  <div className="my-2 text-muted-foreground">
                    Read more about these costs in our{" "}
                    <Link to="https://docs.myth.finance/dualSTAKE/farming#cost">
                      documentation
                    </Link>
                    .
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </>
          ) : null}

          {!apiLoading ? null : <Loading noLogo={true} />}

          {!aprBreakdown || apiLoading ? null : (
            <>
              <div className="text-muted-foreground -mb-1">Rewards impact</div>
              <div className="self-center flex flex flex-col w-fit mb-1 text-muted-foreground">
                <div className="flex space-between gap-8">
                  <div className="">Farm APR</div>
                  <div className="flex-grow flex justify-end gap-2 items-center">
                    {!aprBreakdown!.farmAprBps ? null : (
                      <>
                        {formatNumber(aprBreakdown!.farmAprBps, 2, 1)}%{" "}
                        <ArrowRightIcon size={16} />
                      </>
                    )}{" "}
                    <span className="text-foreground">
                      {formatNumber(aprBreakdown!.overrideFarmAprBps, 2, 1)}%
                    </span>
                  </div>
                </div>
                <div className="flex space-between gap-8">
                  <div>Total APR</div>
                  <div className="flex-grow flex justify-end gap-2 items-center">
                    {formatNumber(
                      aprBreakdown!.netBaseAprBps + aprBreakdown!.farmAprBps,
                      2,
                      1
                    )}
                    % <ArrowRightIcon size={16} />
                    <span className="text-foreground">
                      {formatNumber(
                        aprBreakdown!.netBaseAprBps +
                          aprBreakdown!.overrideFarmAprBps,
                        2,
                        1
                      )}
                      %
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      ) : null}

      <DialogFooter className="sm:justify-end mt-4 gap-2">
        <Button variant="outline" onClick={() => setMode("index")}>
          Go back
        </Button>
        {!aprBreakdown && !farmCost ? (
          <Button
            onClick={calculateCost}
            disabled={
              (mode === "extenda" && !amountPerBlock) ||
              (mode === "extendd" && !durationBlocks) ||
              (mode === "create" && (!durationBlocks || !amountPerBlock)) ||
              apiLoading ||
              !!amountError ||
              !!durationError
            }
          >
            <CalculatorIcon /> Calculate cost
          </Button>
        ) : mode === "create" ? (
          <TransactionDialog
            type="farm creation"
            trigger={(onClick) => (
              <ConnectButton
                onClick={onClick}
                disabled={
                  !(formattedAmount && formattedAsaAmount) ||
                  !!costError ||
                  !!durationError
                }
              >
                <Hammer size={20} /> Create farm
              </ConnectButton>
            )}
            transactionBuilder={() =>
              makeCreateFarmTransactions(activeAddress!, {
                farmAssetId: BigInt(asaId),
                durationBlocks: BigInt(durationBlocks!),
                amountPerBlock: BigInt(
                  Math.round(Number(amountPerBlock!) * 10 ** asaDecimals)
                ),
                dsAppId: appId,
              })
            }
            title={<>Create {lstName} farm</>}
            onSuccess={onSuccess}
          >
            {transactionInner(
              "farm creation",
              <>
                You are going to <strong>donate {asaUnitName}</strong> to create
                a farm for {lstName}.
              </>,
              <>
                <div className="flex justify-between gap-2 mb-2">
                  <div className="flex items-center">
                    <UserRound size={18} className="mr-1" /> Account
                  </div>{" "}
                  <span>{shorten(activeAddress!)}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <div className="flex items-center">
                    <ChevronRight size={18} className="mr-1" /> Donating
                  </div>
                  <span>{formattedAmount} ALGO</span>
                </div>
                <div className="flex justify-between gap-2">
                  <div className="flex items-center">
                    <ChevronRight size={18} className="mr-1" /> Donating
                  </div>
                  <span>
                    {formattedAsaAmount} {asaUnitName}
                  </span>
                </div>
                <div className="flex justify-between gap-2">
                  <div className="flex items-center">
                    <ChevronRight size={18} className="mr-1" />
                    App call
                  </div>
                  <span className="text-right text-muted-foreground">
                    App <ApplicationLink id={dsFarmAppId} />
                  </span>
                </div>
              </>
            )}
          </TransactionDialog>
        ) : (
          <TransactionDialog
            type="farm extension"
            trigger={(onClick) => (
              <ConnectButton
                onClick={onClick}
                disabled={
                  !!amountError ||
                  !!durationError ||
                  (mode === "extenda" && !extendAmountCost) ||
                  (mode === "extendd" && !durationBlocks)
                }
              >
                {mode === "extenda" ? (
                  <CircleDollarSign size={20} />
                ) : (
                  <ClockArrowUp size={20} />
                )}
                Extend farm
              </ConnectButton>
            )}
            transactionBuilder={() =>
              mode === "extenda"
                ? makeExtendAmountTransactions(activeAddress!, {
                    amountPerBlock: BigInt(
                      Math.floor(
                        extendAmountCost /
                          Number(farmState?.remainingDurationBlocks || 1)
                      )
                    ),
                    dsAppId: appId,
                  })
                : makeExtendDurationTransactions(activeAddress!, {
                    dsAppId: appId,
                    extendDurationInBlocks:
                      BigInt(durationBlocks) -
                      farmState!.remainingDurationBlocks,
                  })
            }
            title={<>Extend {lstName} farm</>}
            onSuccess={onSuccess}
          >
            {transactionInner(
              "farm extension",
              <>
                You are going to <strong>donate {asaUnitName}</strong> to
                increase the {lstName} farm{" "}
                {mode === "extenda" ? <>amount per block.</> : <>duration.</>}
              </>,
              <>
                <div className="flex justify-between gap-2 mb-2">
                  <div className="flex items-center">
                    <UserRound size={18} className="mr-1" /> Account
                  </div>{" "}
                  <span>{shorten(activeAddress!)}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <div className="flex items-center">
                    <ChevronRight size={18} className="mr-1" /> Donating
                  </div>
                  <span>
                    {mode === "extenda"
                      ? `${formatNumber(
                          extendAmountCost,
                          asaDecimals,
                          asaDecimals
                        )} ${asaUnitName}`
                      : `${formattedAsaAmount} ${asaUnitName}`}
                  </span>
                </div>
                {mode === "extenda" ? null : (
                  <div className="flex justify-between gap-2">
                    <div className="flex items-center">
                      <ChevronRight size={18} className="mr-1" /> Donating
                    </div>
                    <span>{formattedAmount} ALGO</span>
                  </div>
                )}
                <div className="flex justify-between gap-2">
                  <div className="flex items-center">
                    <ChevronRight size={18} className="mr-1" />
                    App call
                  </div>
                  <span className="text-right text-muted-foreground">
                    App <ApplicationLink id={dsFarmAppId} />
                  </span>
                </div>
              </>
            )}
          </TransactionDialog>
        )}
      </DialogFooter>
    </>
  );
}
